import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import Card from '../common/Card';
import Button from '../common/Button';
import Input from '../common/Input';
import api from '../../services/api';
import {
  HOMEPAGE_DEFAULTS,
  deepMergeHomepage,
  resolveMediaUrl
} from '../../constants/homepageDefaults';
import '../../styles/admin.css';

const TABS = [
  { id: 'brand', label: 'Brand & Logo' },
  { id: 'hero', label: 'Hero' },
  { id: 'demo', label: 'Demo Videos' },
  { id: 'features', label: 'Features' },
  { id: 'pricing', label: 'Pricing' },
  { id: 'testimonials', label: 'Testimonials' },
  { id: 'cta', label: 'CTA' },
  { id: 'footer', label: 'Footer' }
];

const HomepageManager = () => {
  const [tab, setTab] = useState('brand');
  const [content, setContent] = useState(deepMergeHomepage(HOMEPAGE_DEFAULTS));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    setLoading(true);
    try {
      const res = await api.get('/site-content.php');
      const merged = deepMergeHomepage(res.data.content || {});
      setContent(merged);
      setLogoPreview(resolveMediaUrl(merged.brand?.logoUrl));
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || 'Failed to load homepage content');
      setContent(deepMergeHomepage(HOMEPAGE_DEFAULTS));
    } finally {
      setLoading(false);
    }
  };

  const setSection = (section, patch) => {
    setContent((prev) => ({
      ...prev,
      [section]: { ...prev[section], ...patch }
    }));
  };

  const updateListItem = (section, listKey, index, patch) => {
    setContent((prev) => {
      const list = [...(prev[section][listKey] || [])];
      list[index] = { ...list[index], ...patch };
      return { ...prev, [section]: { ...prev[section], [listKey]: list } };
    });
  };

  const addListItem = (section, listKey, blank) => {
    setContent((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [listKey]: [...(prev[section][listKey] || []), blank]
      }
    }));
  };

  const removeListItem = (section, listKey, index) => {
    setContent((prev) => {
      const list = [...(prev[section][listKey] || [])];
      list.splice(index, 1);
      return { ...prev, [section]: { ...prev[section], [listKey]: list } };
    });
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (logoFile) {
        const form = new FormData();
        form.append('site_logo', logoFile);
        form.append('content', JSON.stringify(content));
        const res = await api.post('/site-content.php', form, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        const merged = deepMergeHomepage(res.data.content || content);
        setContent(merged);
        setLogoPreview(resolveMediaUrl(merged.brand?.logoUrl));
        setLogoFile(null);
      } else {
        const res = await api.put('/site-content.php', content);
        setContent(deepMergeHomepage(res.data.content || content));
      }
      toast.success('Homepage content saved');
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleResetDefaults = () => {
    if (!window.confirm('Reset all homepage content to Harvyq ERP defaults?')) return;
    setContent(deepMergeHomepage(HOMEPAGE_DEFAULTS));
    setLogoFile(null);
    setLogoPreview('');
    toast('Defaults loaded — click Save to apply');
  };

  if (loading) {
    return (
      <div className="admin-page">
        <Card>
          <p>Loading homepage content…</p>
        </Card>
      </div>
    );
  }

  const { brand, hero, demo, features, pricing, testimonials, cta, footer } = content;

  return (
    <div className="admin-page homepage-manager">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap' }}>
        <div>
          <h1>Homepage Content</h1>
          <p className="page-subtitle">
            Manage Harvyq landing page — product of <strong>Boldcomp Solutions</strong> (ERP platform)
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <Button variant="secondary" onClick={handleResetDefaults}>
            Reset Defaults
          </Button>
          <Button variant="primary" loading={saving} onClick={handleSave}>
            Save Changes
          </Button>
        </div>
      </div>

      <div className="homepage-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`homepage-tab ${tab === t.id ? 'active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'brand' && (
        <Card title="Brand & Logo">
          <div className="form-grid">
            <Input
              label="Product name"
              name="productName"
              value={brand.productName}
              onChange={(e) => setSection('brand', { productName: e.target.value })}
            />
            <Input
              label="Parent company"
              name="parentCompany"
              value={brand.parentCompany}
              onChange={(e) => setSection('brand', { parentCompany: e.target.value })}
            />
            <Input
              label="Tagline"
              name="tagline"
              value={brand.tagline}
              onChange={(e) => setSection('brand', { tagline: e.target.value })}
            />
            <Input
              label="Logo alt text"
              name="logoAlt"
              value={brand.logoAlt}
              onChange={(e) => setSection('brand', { logoAlt: e.target.value })}
            />
          </div>
          <div className="logo-upload-block">
            <label className="input-label">Site logo</label>
            <div className="logo-upload-row">
              {logoPreview ? (
                <img src={logoPreview} alt="Logo preview" className="logo-preview" />
              ) : (
                <div className="logo-preview placeholder">No logo</div>
              )}
              <div>
                <input type="file" accept="image/*,.svg" onChange={handleLogoChange} />
                <p className="hint">PNG, JPG, WebP or SVG — max 5MB. Saved on Save Changes.</p>
                {brand.logoUrl && (
                  <Input
                    label="Logo URL (stored)"
                    name="logoUrl"
                    value={brand.logoUrl}
                    onChange={(e) => {
                      setSection('brand', { logoUrl: e.target.value });
                      setLogoPreview(resolveMediaUrl(e.target.value));
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </Card>
      )}

      {tab === 'hero' && (
        <Card title="Hero section">
          <Input
            label="Headline"
            name="headline"
            value={hero.headline}
            onChange={(e) => setSection('hero', { headline: e.target.value })}
          />
          <Input
            type="textarea"
            label="Subheadline"
            name="subheadline"
            rows={3}
            value={hero.subheadline}
            onChange={(e) => setSection('hero', { subheadline: e.target.value })}
          />
          <div className="form-grid">
            <Input
              label="Primary CTA label"
              name="primaryCta"
              value={hero.primaryCta}
              onChange={(e) => setSection('hero', { primaryCta: e.target.value })}
            />
            <Input
              label="Primary CTA link"
              name="primaryCtaLink"
              value={hero.primaryCtaLink}
              onChange={(e) => setSection('hero', { primaryCtaLink: e.target.value })}
            />
            <Input
              label="Secondary CTA label"
              name="secondaryCta"
              value={hero.secondaryCta}
              onChange={(e) => setSection('hero', { secondaryCta: e.target.value })}
            />
            <Input
              label="Secondary CTA link"
              name="secondaryCtaLink"
              value={hero.secondaryCtaLink}
              onChange={(e) => setSection('hero', { secondaryCtaLink: e.target.value })}
            />
            <Input
              label="Hero image URL"
              name="imageUrl"
              value={hero.imageUrl}
              onChange={(e) => setSection('hero', { imageUrl: e.target.value })}
            />
          </div>
          <h3>Stats</h3>
          {(hero.stats || []).map((stat, i) => (
            <div key={i} className="list-row form-grid">
              <Input
                label="Number"
                name={`stat-n-${i}`}
                value={stat.number}
                onChange={(e) => updateListItem('hero', 'stats', i, { number: e.target.value })}
              />
              <Input
                label="Label"
                name={`stat-l-${i}`}
                value={stat.label}
                onChange={(e) => updateListItem('hero', 'stats', i, { label: e.target.value })}
              />
              <Button variant="danger" size="sm" onClick={() => removeListItem('hero', 'stats', i)}>
                Remove
              </Button>
            </div>
          ))}
          <Button
            variant="secondary"
            size="sm"
            onClick={() => addListItem('hero', 'stats', { number: '', label: '' })}
          >
            Add stat
          </Button>
        </Card>
      )}

      {tab === 'demo' && (
        <Card title="Demo videos">
          <label className="checkbox-row">
            <input
              type="checkbox"
              checked={!!demo.enabled}
              onChange={(e) => setSection('demo', { enabled: e.target.checked })}
            />
            Show demo section on homepage
          </label>
          <Input
            label="Section title"
            name="demoTitle"
            value={demo.sectionTitle}
            onChange={(e) => setSection('demo', { sectionTitle: e.target.value })}
          />
          <Input
            label="Section subtitle"
            name="demoSubtitle"
            value={demo.sectionSubtitle}
            onChange={(e) => setSection('demo', { sectionSubtitle: e.target.value })}
          />
          {(demo.videos || []).map((video, i) => (
            <div key={i} className="nested-card">
              <div className="nested-card-header">
                <h4>Video {i + 1}</h4>
                <Button variant="danger" size="sm" onClick={() => removeListItem('demo', 'videos', i)}>
                  Remove
                </Button>
              </div>
              <Input
                label="Title"
                name={`video-title-${i}`}
                value={video.title}
                onChange={(e) => updateListItem('demo', 'videos', i, { title: e.target.value })}
              />
              <Input
                label="Video URL (YouTube, Vimeo, or direct)"
                name={`video-url-${i}`}
                value={video.url}
                placeholder="https://www.youtube.com/watch?v=..."
                onChange={(e) => updateListItem('demo', 'videos', i, { url: e.target.value })}
              />
              <Input
                type="textarea"
                label="Description"
                name={`video-desc-${i}`}
                rows={2}
                value={video.description}
                onChange={(e) =>
                  updateListItem('demo', 'videos', i, { description: e.target.value })
                }
              />
            </div>
          ))}
          <Button
            variant="secondary"
            size="sm"
            onClick={() =>
              addListItem('demo', 'videos', { title: '', url: '', description: '' })
            }
          >
            Add video
          </Button>
        </Card>
      )}

      {tab === 'features' && (
        <Card title="Features">
          <Input
            label="Section title"
            name="featTitle"
            value={features.sectionTitle}
            onChange={(e) => setSection('features', { sectionTitle: e.target.value })}
          />
          <Input
            label="Section subtitle"
            name="featSubtitle"
            value={features.sectionSubtitle}
            onChange={(e) => setSection('features', { sectionSubtitle: e.target.value })}
          />
          {(features.items || []).map((item, i) => (
            <div key={i} className="nested-card">
              <div className="nested-card-header">
                <h4>Feature {i + 1}</h4>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => removeListItem('features', 'items', i)}
                >
                  Remove
                </Button>
              </div>
              <div className="form-grid">
                <Input
                  label="Icon (emoji)"
                  name={`feat-icon-${i}`}
                  value={item.icon}
                  onChange={(e) => updateListItem('features', 'items', i, { icon: e.target.value })}
                />
                <Input
                  label="Title"
                  name={`feat-title-${i}`}
                  value={item.title}
                  onChange={(e) => updateListItem('features', 'items', i, { title: e.target.value })}
                />
              </div>
              <Input
                type="textarea"
                label="Description"
                name={`feat-desc-${i}`}
                rows={2}
                value={item.description}
                onChange={(e) =>
                  updateListItem('features', 'items', i, { description: e.target.value })
                }
              />
            </div>
          ))}
          <Button
            variant="secondary"
            size="sm"
            onClick={() =>
              addListItem('features', 'items', { icon: '✨', title: '', description: '' })
            }
          >
            Add feature
          </Button>
        </Card>
      )}

      {tab === 'pricing' && (
        <Card title="Pricing cards">
          <Input
            label="Section title"
            name="priceTitle"
            value={pricing.sectionTitle}
            onChange={(e) => setSection('pricing', { sectionTitle: e.target.value })}
          />
          <Input
            label="Section subtitle"
            name="priceSubtitle"
            value={pricing.sectionSubtitle}
            onChange={(e) => setSection('pricing', { sectionSubtitle: e.target.value })}
          />
          {(pricing.plans || []).map((plan, i) => (
            <div key={i} className="nested-card">
              <div className="nested-card-header">
                <h4>{plan.name || `Plan ${i + 1}`}</h4>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => removeListItem('pricing', 'plans', i)}
                >
                  Remove
                </Button>
              </div>
              <div className="form-grid">
                <Input
                  label="Name"
                  name={`plan-name-${i}`}
                  value={plan.name}
                  onChange={(e) => updateListItem('pricing', 'plans', i, { name: e.target.value })}
                />
                <Input
                  label="Price"
                  name={`plan-price-${i}`}
                  value={plan.price}
                  onChange={(e) => updateListItem('pricing', 'plans', i, { price: e.target.value })}
                />
                <Input
                  label="Period"
                  name={`plan-period-${i}`}
                  value={plan.period}
                  onChange={(e) => updateListItem('pricing', 'plans', i, { period: e.target.value })}
                />
                <Input
                  label="CTA"
                  name={`plan-cta-${i}`}
                  value={plan.cta}
                  onChange={(e) => updateListItem('pricing', 'plans', i, { cta: e.target.value })}
                />
              </div>
              <label className="checkbox-row">
                <input
                  type="checkbox"
                  checked={!!plan.popular}
                  onChange={(e) =>
                    updateListItem('pricing', 'plans', i, { popular: e.target.checked })
                  }
                />
                Mark as most popular
              </label>
              <Input
                type="textarea"
                label="Features (one per line)"
                name={`plan-features-${i}`}
                rows={5}
                value={(plan.features || []).join('\n')}
                onChange={(e) =>
                  updateListItem('pricing', 'plans', i, {
                    features: e.target.value.split('\n').map((l) => l.trim()).filter(Boolean)
                  })
                }
              />
            </div>
          ))}
          <Button
            variant="secondary"
            size="sm"
            onClick={() =>
              addListItem('pricing', 'plans', {
                name: 'New Plan',
                price: '0',
                period: 'month',
                features: [],
                cta: 'Start Free Trial',
                popular: false
              })
            }
          >
            Add plan
          </Button>
        </Card>
      )}

      {tab === 'testimonials' && (
        <Card title="Testimonials">
          <Input
            label="Section title"
            name="testTitle"
            value={testimonials.sectionTitle}
            onChange={(e) => setSection('testimonials', { sectionTitle: e.target.value })}
          />
          <Input
            label="Section subtitle"
            name="testSubtitle"
            value={testimonials.sectionSubtitle}
            onChange={(e) => setSection('testimonials', { sectionSubtitle: e.target.value })}
          />
          {(testimonials.items || []).map((item, i) => (
            <div key={i} className="nested-card">
              <div className="nested-card-header">
                <h4>Testimonial {i + 1}</h4>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => removeListItem('testimonials', 'items', i)}
                >
                  Remove
                </Button>
              </div>
              <div className="form-grid">
                <Input
                  label="Name"
                  name={`t-name-${i}`}
                  value={item.name}
                  onChange={(e) =>
                    updateListItem('testimonials', 'items', i, { name: e.target.value })
                  }
                />
                <Input
                  label="Business"
                  name={`t-biz-${i}`}
                  value={item.business}
                  onChange={(e) =>
                    updateListItem('testimonials', 'items', i, { business: e.target.value })
                  }
                />
                <Input
                  label="Rating (1–5)"
                  name={`t-rating-${i}`}
                  type="number"
                  min={1}
                  max={5}
                  value={item.rating}
                  onChange={(e) =>
                    updateListItem('testimonials', 'items', i, {
                      rating: Math.min(5, Math.max(1, parseInt(e.target.value, 10) || 5))
                    })
                  }
                />
              </div>
              <Input
                type="textarea"
                label="Quote"
                name={`t-content-${i}`}
                rows={3}
                value={item.content}
                onChange={(e) =>
                  updateListItem('testimonials', 'items', i, { content: e.target.value })
                }
              />
            </div>
          ))}
          <Button
            variant="secondary"
            size="sm"
            onClick={() =>
              addListItem('testimonials', 'items', {
                name: '',
                business: '',
                content: '',
                rating: 5
              })
            }
          >
            Add testimonial
          </Button>
        </Card>
      )}

      {tab === 'cta' && (
        <Card title="Bottom CTA">
          <Input
            label="Headline"
            name="ctaHeadline"
            value={cta.headline}
            onChange={(e) => setSection('cta', { headline: e.target.value })}
          />
          <Input
            type="textarea"
            label="Subtext"
            name="ctaSubtext"
            rows={2}
            value={cta.subtext}
            onChange={(e) => setSection('cta', { subtext: e.target.value })}
          />
          <div className="form-grid">
            <Input
              label="Button text"
              name="ctaButton"
              value={cta.buttonText}
              onChange={(e) => setSection('cta', { buttonText: e.target.value })}
            />
            <Input
              label="Button link"
              name="ctaLink"
              value={cta.buttonLink}
              onChange={(e) => setSection('cta', { buttonLink: e.target.value })}
            />
            <Input
              label="Footnote"
              name="ctaFoot"
              value={cta.footnote}
              onChange={(e) => setSection('cta', { footnote: e.target.value })}
            />
          </div>
        </Card>
      )}

      {tab === 'footer' && (
        <Card title="Footer">
          <Input
            type="textarea"
            label="About text"
            name="footerAbout"
            rows={3}
            value={footer.about}
            onChange={(e) => setSection('footer', { about: e.target.value })}
          />
          <div className="form-grid">
            <Input
              label="Phone"
              name="footerPhone"
              value={footer.phone}
              onChange={(e) => setSection('footer', { phone: e.target.value })}
            />
            <Input
              label="Email"
              name="footerEmail"
              value={footer.email}
              onChange={(e) => setSection('footer', { email: e.target.value })}
            />
            <Input
              label="Address"
              name="footerAddress"
              value={footer.address}
              onChange={(e) => setSection('footer', { address: e.target.value })}
            />
            <Input
              label="Copyright line"
              name="footerCopyright"
              value={footer.copyright}
              onChange={(e) => setSection('footer', { copyright: e.target.value })}
            />
          </div>
          <h3>Social links</h3>
          <div className="form-grid">
            {['facebook', 'twitter', 'instagram', 'linkedin'].map((key) => (
              <Input
                key={key}
                label={key.charAt(0).toUpperCase() + key.slice(1)}
                name={`social-${key}`}
                value={footer.social?.[key] || ''}
                onChange={(e) =>
                  setSection('footer', {
                    social: { ...footer.social, [key]: e.target.value }
                  })
                }
              />
            ))}
          </div>
        </Card>
      )}

      <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="primary" loading={saving} onClick={handleSave}>
          Save Changes
        </Button>
      </div>
    </div>
  );
};

export default HomepageManager;
